import React, { useState } from 'react';
import { Card, Form, Input, Button, Typography, Space, notification } from 'antd';
import { LockOutlined, SaveOutlined } from '@ant-design/icons';
import axiosClient from '../../api/axiosClient';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

const ChangePassword = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const logout = async () => {
        const role = localStorage.getItem("USERROLE");
        const logoutUrl = role === "MEMBER" ? '/portal/logout.php' : '/user/logout.php';
        try {
            await axiosClient.post(logoutUrl);
        } catch (error) {
            console.error("Logout failed", error);
        } finally {
            localStorage.clear();
            navigate('/login');
        }
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const response = await axiosClient.post('/portal/change_password.php', {
                old_password: values.oldPassword,
                new_password: values.newPassword
            });

            if (response.data.status === 'success') {
                notification.success({
                    message: 'Success',
                    description: 'Password updated successfully. Please log in again.',
                });
                form.resetFields();
                logout(); // Logout for security
            } else {
                notification.error({
                    message: 'Error',
                    description: response.data.message || 'Failed to update password.',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'An error occurred. Please try again later.',
            });
        } finally {
            setLoading(false);
        }
    };

    const cardStyle = {
        maxWidth: 500,
        margin: '60px auto',
        borderRadius: 24,
        border: 'none',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.05)',
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
    };

    return (
        <div style={{ width: '100%', padding: '0 16px' }}>
            <Card style={cardStyle} bodyStyle={{ padding: '40px 32px' }}>
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <div style={{ 
                        width: 64, 
                        height: 64, 
                        borderRadius: '20px', 
                        background: '#f0fcfb', 
                        margin: '0 auto 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#209c96',
                        boxShadow: '0 4px 12px rgba(32,156,150,0.1)'
                    }}>
                        <LockOutlined style={{ fontSize: 32 }} />
                    </div>
                    <Title level={3} style={{ margin: 0, fontWeight: 800 }}>Update Security</Title>
                    <Text type="secondary">Keep your account safe by updating your password regularly.</Text>
                </div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    requiredMark={false}
                    size="large"
                >
                    <Form.Item
                        name="oldPassword"
                        label={<Text strong>Current Password</Text>}
                        rules={[{ required: true, message: 'Please enter your current password' }]}
                    >
                        <Input.Password 
                            prefix={<LockOutlined style={{ color: '#bfbfbf' }} />} 
                            placeholder="Type current password" 
                            style={{ borderRadius: 12 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="newPassword"
                        label={<Text strong>New Password</Text>}
                        rules={[
                            { required: true, message: 'Please enter a new password' },
                            { min: 8, message: 'Password must be at least 8 characters long' }
                        ]}
                    >
                        <Input.Password 
                            prefix={<LockOutlined style={{ color: '#bfbfbf' }} />} 
                            placeholder="Min 8 characters" 
                            style={{ borderRadius: 12 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="confirmPassword"
                        label={<Text strong>Confirm New Password</Text>}
                        dependencies={['newPassword']}
                        rules={[
                            { required: true, message: 'Please confirm your new password' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Passwords do not match!'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password 
                            prefix={<LockOutlined style={{ color: '#bfbfbf' }} />} 
                            placeholder="Re-type new password" 
                            style={{ borderRadius: 12 }}
                        />
                    </Form.Item>

                    <Form.Item style={{ marginTop: 40, marginBottom: 0 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            block
                            icon={<SaveOutlined />}
                            style={{
                                height: 50,
                                borderRadius: 12,
                                fontSize: 16,
                                fontWeight: 600,
                                background: 'linear-gradient(90deg, #209c96 0%, #167d78 100%)',
                                border: 'none',
                                boxShadow: '0 4px 12px rgba(32,156,150,0.3)'
                            }}
                        >
                            Update Password
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default ChangePassword;
